import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import { Link } from 'react-router';
import { getSigningApiUrl, getSigningAwsAccount } from '../amplify-config';
import { storeSignerSession } from '../auth/signer-session';
import LoadingPage from '../components/LoadingPage';
import { resolveNextOrDefault } from '../utils/safe-next';

/**
 * Passwordless magic-link login for email-only signers (#2955, Phase 3/5).
 *
 * This flow is now Cognito-free: the magic-link token is exchanged for a
 * self-issued signer JWT via `POST /api/auth/session`. The JWT is stored in
 * sessionStorage (the default non-"remember me" backend) and attached as a
 * `Bearer` token on subsequent API calls, verified in-account by the Phase-2
 * authorizer. There is NO Cognito CUSTOM_AUTH call in this path.
 *
 * Admins and SSO signers are unaffected — they never reach this page (#2848);
 * they sign in through Cognito on the standard /login form.
 *
 * Flow:
 * 1. Extract `:token` from URL params.
 * 2. POST the token to `/auth/session`.
 * 3. On 200, store the returned JWT and navigate to the `?next=` target (or
 *    `/signer`). On any non-200, show the appropriate error.
 */
function LoginByToken() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [missingToken, setMissingToken] = useState(false);

    /**
     * Post-auth navigation target derived from the `?next=` query parameter
     * (issue #1477). `resolveNextOrDefault` enforces the same-origin contract;
     * see `utils/safe-next.ts`. `decodeURIComponent` may throw on malformed
     * input — that case falls back to the safe default.
     */
    const nextTarget = useMemo(() => {
        const fallback = '/signer';
        const rawValue = searchParams.get('next');
        if (rawValue === null) return fallback;
        let decoded: string | null;
        try {
            decoded = decodeURIComponent(rawValue);
        } catch {
            decoded = null;
        }
        return resolveNextOrDefault(decoded, fallback);
    }, [searchParams]);

    const exchangeTokenForSession = useCallback(async () => {
        if (!token) {
            setMissingToken(true);
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${getSigningApiUrl()}/auth/session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Aws-Account': getSigningAwsAccount(),
                },
                body: JSON.stringify({ token }),
            });

            if (!response.ok) {
                if (response.status === 403) {
                    setError('This account cannot sign in with a magic link. Contact your administrator.');
                } else {
                    // 401 (invalid / expired / already-used) and anything else
                    // surface the generic invalid-link message.
                    setError('This sign-in link is invalid or has already been used. Please request a new one.');
                }
                setLoading(false);
                return;
            }

            const data = (await response.json()) as { token: string };
            if (!data.token) {
                setError('This sign-in link is invalid or has already been used. Please request a new one.');
                setLoading(false);
                return;
            }

            // Store the self-issued signer JWT in sessionStorage and navigate.
            storeSignerSession(data.token);
            navigate(nextTarget, { replace: true });
        } catch {
            setError('Failed to load the sign-in link. Please check your connection and try again.');
            setLoading(false);
        }
    }, [token, navigate, nextTarget]);

    useEffect(() => {
        exchangeTokenForSession();
    }, [exchangeTokenForSession]);

    if (missingToken) {
        return (
            <Container
                maxWidth="sm"
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', py: 4 }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Invalid Sign-In Link
                    </Typography>
                    <Typography role="alert" aria-live="assertive" color="text.secondary" sx={{ mt: 1 }}>
                        The sign-in link is missing or invalid.
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                        <Button component={Link} to="/login" variant="outlined">
                            Back to Login
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    if (loading) {
        return <LoadingPage message="Signing in..." />;
    }

    if (error) {
        return (
            <Container
                maxWidth="sm"
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', py: 4 }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Unable to Sign In
                    </Typography>
                    <Alert severity="error" role="alert" aria-live="assertive" sx={{ mt: 1, textAlign: 'left' }}>
                        {error}
                    </Alert>
                    <Box sx={{ mt: 3 }}>
                        <Button component={Link} to="/login" variant="outlined">
                            Back to Login
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    return <LoadingPage message="Authenticating..." />;
}

export default LoginByToken;
